"use client";

import Image from "next/image";
import { useState, type KeyboardEvent } from "react";
import {
  Briefcase,
  ChartLine,
  FileText,
  Receipt,
  type LucideIcon,
} from "lucide-react";

import { Reveal } from "@/components/landing/reveal";

/*
 * AppShowcase — "see the product" section. Accessible hand-rolled tabs
 * switch between four large screen mockups rendered as pure JSX inside
 * the brand bezel. Remounting the panel (key) replays the rise-in
 * animation for a soft crossfade feel without extra JS.
 */

type Pill = { label: string; tone: string };

const PILL = {
  paid: { label: "Zapłacona", tone: "bg-success/10 text-success" },
  due: { label: "Termin 14.07", tone: "bg-warning/10 text-warning" },
  overdue: { label: "Po terminie", tone: "bg-destructive/10 text-destructive" },
  draft: { label: "Do akceptacji", tone: "bg-primary/10 text-primary" },
  sent: { label: "Wysłana", tone: "bg-primary/10 text-primary" },
  accepted: { label: "Zaakceptowana", tone: "bg-success/10 text-success" },
  inProgress: { label: "W realizacji", tone: "bg-primary/10 text-primary" },
  done: { label: "Zakończony", tone: "bg-success/10 text-success" },
} satisfies Record<string, Pill>;

type Row = {
  id: string;
  name: string;
  amount: string;
  pill: Pill;
  /** 0–100, used by screens that show a progress bar instead of a pill. */
  progress?: number;
};

type Screen = {
  key: string;
  tab: string;
  icon: LucideIcon;
  title: string;
  caption: string;
  columns: [string, string, string, string];
  rows: Row[];
  summary: { label: string; value: string }[];
};

const SCREENS: Screen[] = [
  {
    key: "pulpit",
    tab: "Pulpit",
    icon: ChartLine,
    title: "Pulpit finansowy",
    caption:
      "Przychody, koszty i marża firmy w jednym widoku — liczone wyłącznie z zaakceptowanych dokumentów.",
    columns: ["Projekt", "Klient", "Wynik", "Status"],
    rows: [
      {
        id: "P/26/07/0A2K",
        name: "Biurowiec Alfa — instalacje elektryczne",
        amount: "+38 450 zł",
        pill: PILL.inProgress,
      },
      {
        id: "P/26/06/9XT1",
        name: "Osiedle Zielona — pompy ciepła",
        amount: "+21 730 zł",
        pill: PILL.inProgress,
      },
      {
        id: "P/26/05/4RD8",
        name: "Hala Logis — serwis klimatyzacji",
        amount: "+9 120 zł",
        pill: PILL.done,
      },
    ],
    summary: [
      { label: "Przychód (mies.)", value: "148 230 zł" },
      { label: "Koszty (mies.)", value: "96 410 zł" },
      { label: "Marża", value: "34,9%" },
      { label: "Należności", value: "27 340 zł" },
    ],
  },
  {
    key: "faktury",
    tab: "Faktury",
    icon: Receipt,
    title: "Rejestr faktur",
    caption:
      "Wszystkie faktury kosztowe i przychodowe z terminami płatności — zaimportowane automatycznie, czekają najwyżej na jedno kliknięcie.",
    columns: ["Numer", "Kontrahent", "Kwota", "Status"],
    rows: [
      {
        id: "FV/2026/07/018",
        name: "Elektromont Silesia",
        amount: "12 300 zł",
        pill: PILL.paid,
      },
      {
        id: "FK/2026/07/034",
        name: "Hurtownia El-Max",
        amount: "3 260 zł",
        pill: PILL.draft,
      },
      {
        id: "FV/2026/07/021",
        name: "HydroTech Instalacje",
        amount: "8 940 zł",
        pill: PILL.due,
      },
      {
        id: "FV/2026/06/097",
        name: "KlimaPro Serwis",
        amount: "4 510 zł",
        pill: PILL.overdue,
      },
    ],
    summary: [
      { label: "Do akceptacji", value: "12" },
      { label: "Po terminie", value: "3" },
      { label: "Suma należności", value: "27 340 zł" },
      { label: "Zaimportowane dziś", value: "9" },
    ],
  },
  {
    key: "projekty",
    tab: "Projekty",
    icon: Briefcase,
    title: "Projekty i inwestycje",
    caption:
      "Etapy, budżety i marża każdego projektu aktualizowane z każdą fakturą — widzisz przekroczenia, zanim staną się problemem.",
    columns: ["Numer", "Projekt", "Marża", "Postęp"],
    rows: [
      {
        id: "P/26/07/0A2K",
        name: "Biurowiec Alfa — instalacje",
        amount: "26,4%",
        pill: PILL.inProgress,
        progress: 68,
      },
      {
        id: "P/26/06/9XT1",
        name: "Osiedle Zielona — pompy ciepła",
        amount: "31,2%",
        pill: PILL.inProgress,
        progress: 42,
      },
      {
        id: "P/26/05/4RD8",
        name: "Hala Logis — klimatyzacja",
        amount: "22,8%",
        pill: PILL.done,
        progress: 100,
      },
    ],
    summary: [
      { label: "Aktywne projekty", value: "7" },
      { label: "Średnia marża", value: "27,1%" },
      { label: "Budżety łącznie", value: "1,84 mln zł" },
      { label: "Etapy do odbioru", value: "2" },
    ],
  },
  {
    key: "oferty",
    tab: "Oferty",
    icon: FileText,
    title: "Oferty",
    caption:
      "Szablony, katalog pozycji i wersjonowanie — nowa oferta powstaje w kilka minut i wychodzi jako dopracowany PDF.",
    columns: ["Numer", "Klient", "Wartość", "Status"],
    rows: [
      {
        id: "OF/2026/07/041",
        name: "Novum Projekt — biuro 340 m²",
        amount: "86 200 zł",
        pill: PILL.sent,
      },
      {
        id: "OF/2026/07/038",
        name: "VoltHouse — fotowoltaika 50 kWp",
        amount: "212 400 zł",
        pill: PILL.accepted,
      },
      {
        id: "OF/2026/07/044",
        name: "TermoSystem — węzeł cieplny",
        amount: "54 900 zł",
        pill: PILL.draft,
      },
    ],
    summary: [
      { label: "Wysłane w lipcu", value: "14" },
      { label: "Skuteczność", value: "43%" },
      { label: "Wartość otwartych", value: "612 tys. zł" },
      { label: "Średni czas przygotowania", value: "11 min" },
    ],
  },
];

export function AppShowcase() {
  const [active, setActive] = useState(SCREENS[0].key);
  const screen = SCREENS.find((s) => s.key === active) ?? SCREENS[0];

  // WAI-ARIA tabs: arrow keys / Home / End move selection and focus.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    const nav: Record<string, number> = {
      ArrowRight: (idx + 1) % SCREENS.length,
      ArrowLeft: (idx - 1 + SCREENS.length) % SCREENS.length,
      Home: 0,
      End: SCREENS.length - 1,
    };
    if (!(e.key in nav)) return;
    e.preventDefault();
    const nextKey = SCREENS[nav[e.key]].key;
    setActive(nextKey);
    document.getElementById(`tab-${nextKey}`)?.focus();
  };

  return (
    <section
      id="aplikacja"
      className="border-y border-border bg-secondary/40"
    >
      <div className="mx-auto max-w-6xl px-6 py-24 lg:py-32">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.85fr] lg:gap-12">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-balance text-foreground sm:text-4xl lg:text-5xl">
              Zobacz system od środka
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-pretty text-muted-foreground">
              Jasny, przejrzysty interfejs, który zespół opanuje w jeden dzień —
              bez szkoleń i grubych instrukcji. Dostępny wszędzie tam, gdzie
              pracujesz — także z tabletu na budowie.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-2xl shadow-soft ring-1 ring-border">
              <Image
                src="/brand/landing/field.webp"
                alt="Ekipa sprawdza dane w systemie na tablecie na budowie"
                width={1500}
                height={1006}
                sizes="(max-width: 1024px) 100vw, 480px"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-10">
          {/* Tabs */}
          <div
            role="tablist"
            aria-label="Ekrany aplikacji"
            className="glass inline-flex max-w-full flex-wrap gap-1 rounded-full p-1"
          >
            {SCREENS.map((s, i) => {
              const selected = s.key === active;
              return (
                <button
                  key={s.key}
                  role="tab"
                  id={`tab-${s.key}`}
                  aria-selected={selected}
                  aria-controls={`panel-${s.key}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(s.key)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    selected
                      ? "bg-primary text-primary-foreground shadow-soft"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <s.icon className="size-4" />
                  {s.tab}
                </button>
              );
            })}
          </div>

          {/* Screen */}
          <div
            key={screen.key}
            role="tabpanel"
            id={`panel-${screen.key}`}
            aria-labelledby={`tab-${screen.key}`}
            className="animate-rise mt-6"
          >
            <div className="bezel shadow-glow">
              <div className="overflow-hidden bg-card">
                <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {screen.title}
                    </p>
                    <p className="mt-0.5 hidden text-xs text-muted-foreground sm:block">
                      {screen.caption}
                    </p>
                  </div>
                  <span className="rounded-full bg-secondary px-2.5 py-1 font-mono text-[10px] text-muted-foreground">
                    lipiec 2026
                  </span>
                </div>

                {/* summary tiles */}
                <div className="grid grid-cols-2 gap-2 px-5 pt-4 sm:grid-cols-4">
                  {screen.summary.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl bg-secondary/60 p-3 ring-1 ring-border/60"
                    >
                      <p className="truncate text-[11px] text-muted-foreground">
                        {item.label}
                      </p>
                      <p className="tabular mt-0.5 truncate font-mono text-sm font-semibold text-foreground">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* data table */}
                <div className="overflow-x-auto px-5 py-4">
                  <table className="w-full min-w-[36rem] text-left text-sm">
                    <thead>
                      <tr className="text-[11px] text-muted-foreground">
                        {screen.columns.map((col) => (
                          <th key={col} className="pb-2 font-medium">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {screen.rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-t border-border/70 transition-colors duration-200 hover:bg-secondary/50"
                        >
                          <td className="tabular py-2.5 pr-4 font-mono text-xs text-muted-foreground">
                            {row.id}
                          </td>
                          <td className="max-w-[16rem] truncate py-2.5 pr-4 text-foreground">
                            {row.name}
                          </td>
                          <td className="tabular py-2.5 pr-4 font-mono text-xs font-medium text-foreground">
                            {row.amount}
                          </td>
                          <td className="py-2.5">
                            {row.progress != null ? (
                              <span className="flex items-center gap-2">
                                <span className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary">
                                  <span
                                    className="block h-full rounded-full bg-primary"
                                    style={{ width: `${row.progress}%` }}
                                  />
                                </span>
                                <span className="tabular font-mono text-[11px] text-muted-foreground">
                                  {row.progress}%
                                </span>
                              </span>
                            ) : (
                              <span
                                className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${row.pill.tone}`}
                              >
                                {row.pill.label}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
